<template>
  <h2 class="is-size-2 mb-4 mt-2 title">{{ title }}</h2>
  <form>
    <div class="friendlier-field mt-2">
      <p class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="signature" />
        </span>
        <input
          id="current-email"
          name="email"
          type="email"
          required
          :placeholder="$t('emailUpdateForm.email')"
          v-model="email"
          autocomplete="email"
          inputmode="email"
          autocapitalize="off"
          spellcheck="false"
          class="is-size-4 is-right p-2"
        />
      </p>
      <div class="mt-4 has-text-danger error">{{ emailError }}</div>
    </div>
    <div class="friendlier-field mt-2">
      <p class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="signature" />
        </span>
        <input
          id="new-email"
          name="new-email"
          type="email"
          required
          :placeholder="$t('emailUpdateForm.new-email')"
          v-model="newEmail"
          autocomplete="off"
          inputmode="email"
          autocapitalize="off"
          spellcheck="false"
          class="is-size-4 is-right p-2"
        />
      </p>
      <div class="mt-4 has-text-danger error">{{ newEmailError }}</div>
    </div>
    <div class="friendlier-field mt-2">
      <p class="control">
        <span class="icon is-medium is-left mr-3">
          <FontAwesomeIcon icon="signature" />
        </span>
        <input
          id="confirm-email"
          name="confirm-email"
          type="email"
          required
          :placeholder="$t('emailUpdateForm.confirm-email')"
          v-model="confirmEmail"
          autocomplete="off"
          inputmode="email"
          autocapitalize="off"
          spellcheck="false"
          class="is-size-4 is-right p-2"
        />
      </p>
      <div class="mt-4 has-text-danger error">{{ confirmEmailError }}</div>
    </div>
  </form>
  <button :onclick="validate" :disabled="locked" class="ocean-button is-bold fs-1-2">
    {{ buttonText }}
  </button>
  <div class="mt-4 has-text-danger error">{{ error }}</div>
</template>

<script setup>
import { ref, watch } from "vue";
import { emailValidator, emailMatch } from "@/revamp/utils";
import { useI18n } from "vue-i18n";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
  showEmail: Boolean,
  locked: Boolean,
  title: String,
  buttonText: String,
  emailVal: String,
  submit: Function,
});

const email = ref(props.emailVal ? props.emailVal : "");
const emailError = ref("");
const newEmail = ref("");
const newEmailError = ref("");
const confirmEmail = ref("");
const confirmEmailError = ref("");
const error = ref("");

const { t } = useI18n({
  locale: "en",
  messages: {
    en: {
      validEmail: "Please enter a valid email",
      allFields: "Please complete all fields",
      fix: "Fix problems before submitting",
      emailsMatch: "Emails do not match",
    },
    fr: {
      validEmail: "Veuillez saisir un courriel valide",
      allFields: "Veuillez remplir tous les champs",
      fix: "Réglez les problèmes avant de soumettre",
      emailsMatch: "Les courriels ne correspondent pas",
    },
  },
});

watch(email, () => {
  error.value = "";
  emailError.value = "";
  if (email.value == "") {
    emailError.value = "";
    return;
  }
  if (!emailValidator(email.value)) {
    emailError.value = t("validEmail");
  }
});

watch(newEmail, () => {
  error.value = "";
  newEmailError.value = "";
  if (newEmail.value && !emailValidator(newEmail.value)) {
    newEmailError.value = t("validEmail");
  } else if (newEmail.value && confirmEmail.value && !emailMatch(newEmail.value, confirmEmail.value)) {
    confirmEmailError.value = t("emailsMatch");
  } else {
    confirmEmailError.value = "";
  }
});

watch(confirmEmail, () => {
  error.value = "";
  confirmEmailError.value = "";
  if (confirmEmail.value && !emailValidator(confirmEmail.value)) {
    confirmEmailError.value = t("validEmail");
  } else if (newEmail.value && confirmEmail.value && !emailMatch(newEmail.value, confirmEmail.value)) {
    confirmEmailError.value = t("emailsMatch");
  } else {
    confirmEmailError.value = "";
  }
});

const validate = async () => {
  error.value = "";
  if (email.value == "" || newEmail.value == "" || confirmEmail.value == "") {
    error.value = t("allFields");
    return;
  }
  if (emailError.value != "" || newEmailError.value != "" || confirmEmailError.value != "") {
    error.value = t("fix");
    return;
  }

  props.submit({ email: email.value, newEmail: newEmail.value });
};
</script>

<style lang="scss" scoped>
@import "@/revamp/assets/scss/colors.scss";
@import "@/revamp/assets/scss/elements.scss";

.title {
  color: $sunflower;
}

span.icon {
  width: 20px;
}
</style>
